import styles from './splits-info-popover.module.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { PopoverWithClose } from '@disco/shared-components/ui/popover-with-close'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { getCanChangeBusinessSettings } from '@disco/state-management/selectors/user'

import { Box, Code, IconButton, Link, Text } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  title: string
  description: string
  example: string
}

/**
 * Info popover shown next to a field label when this business writes Rights
 * splits into that field's metadata on export — explains the format and
 * links to where that behavior is configured.
 */
const SplitsInfoPopover = ({
  title,
  description,
  example,
}: Props): React.ReactElement => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const canChangeBusinessSettings = useSelector(getCanChangeBusinessSettings)

  const handleBusinessSettingsClick = (event: React.MouseEvent) => {
    event.preventDefault()
    navigate('/settings/business')
    dispatch(sendMixpanelEvent('Change business settings (modal)'))
  }

  return (
    <PopoverWithClose
      title={title}
      width="280px"
      content={
        <>
          <Text as="p" size="2" color="gray" mb="3">
            {description}
          </Text>
          <Box className={styles.exampleBox} mb="3">
            <Code size="1" variant="ghost">
              {example}
            </Code>
          </Box>
          {canChangeBusinessSettings && (
            <Text as="p" size="2" color="gray">
              Change this in{' '}
              <Link
                href="/settings/business"
                onClick={handleBusinessSettingsClick}
              >
                business settings
              </Link>
              .
            </Text>
          )}
        </>
      }
    >
      <IconButton
        type="button"
        variant="ghost"
        color="gray"
        size="1"
        className={styles.trigger}
        aria-label={`${title} info`}
      >
        <RxInfoCircled size={14} />
      </IconButton>
    </PopoverWithClose>
  )
}

export default SplitsInfoPopover
